// src/app/app.config.ts
import { ApplicationConfig, importProvidersFrom, APP_INITIALIZER, provideZoneChangeDetection, ErrorHandler } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'; // ✅ Lazy load animations
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';

// Third Party imports
import { provideSweetAlert2 } from '@sweetalert2/ngx-sweetalert2';
import { provideNgxMask } from 'ngx-mask';
import { provideNgxMatMomentDate } from '@ngx-cmps/moment-adapter';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { ProgressbarConfig } from 'ngx-bootstrap/progressbar';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ToastrModule } from 'ngx-toastr';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { provideEchartsCore } from 'ngx-echarts';
import { FormlyModule } from '@ngx-formly/core';

// Internal imports
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routing';
import { AxiosConfigFactory, InterceptorService } from '../services/loader/interceptor.service';
import { CustomReuseStrategy } from '../globals/CustomReuseStrategy'; // Check path
import { RouteReuseStrategy } from '@angular/router';
import { AudioRecordingService } from '../services/audio-recording.service';
import { provideSharedComponentTokens } from './shared/tokens/component-token.providers';
import { AppErrorHandler } from './core/app-error.handler';
import { AppVersionService } from './core/app-version.service';
import { ClockSkewService } from './core/clock-skew.service';
import {
  SelectContactComponent,
  SelectLeadTagComponent,
  SelectAddressComponentComponent,
  SelectLeadSourceComponent,
  SelectEmployeeComponent,
  SelectTeamComponent,
  SelectBusinessComponent,
  SelectIndustryComponent,
  SelectProductServiceComponent,
  SelectAccountComponent,
  SelectDealComponent,
  SelectCustomAttributeValueComponent,
  RuleTypeComponent,
  RulesetTypeComponent,
  RulesetRulesTypeComponent,
  SelectGenricFormlyComponent,
  GenericDateSetterComponent
} from './shared/formly/formly-types';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = { suppressScrollX: true };
const DRAG_CONFIG = { dragStartThreshold: 0, pointerDirectionChangeThreshold: 5, zIndex: 100000 };

function initializeAppVersion(appVersion: AppVersionService): () => void {
  return () => appVersion.start();
}

function initializeClockSkew(clockSkew: ClockSkewService): () => void {
  return () => clockSkew.start();
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' } // 'fixed' is default, 'dynamic' collapses space
    },
    // 1. Core Providers
    provideAnimationsAsync(), // ⚡ Lazy load animations
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideHttpClient(withInterceptorsFromDi()),

    // 2. Router
    provideRouter(
      APP_ROUTES,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withViewTransitions() // ✨ Smooth transitions between pages
    ),

    // 3. Module Imports (Legacy)
    importProvidersFrom(
      // ❌ REMOVED BrowserModule (It is built-in to bootstrapApplication)
      LoggerModule.forRoot({ level: !environment.production ? NgxLoggerLevel.DEBUG : NgxLoggerLevel.OFF }),
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-left',
        newestOnTop: false,
        timeOut: 3000,
        progressBar: true,
        closeButton: true,
        preventDuplicates: true,
      }),
      LoadingBarModule,
      LoadingBarRouterModule,
      FormlyModule.forRoot({
        types: [
          { name: 'contactSelect', component: SelectContactComponent },
          { name: 'tagSelect', component: SelectLeadTagComponent },
          { name: 'addressSelect', component: SelectAddressComponentComponent },
          { name: 'leadSourceSelect', component: SelectLeadSourceComponent },
          { name: 'userSelect', component: SelectEmployeeComponent },
          { name: 'teamSelect', component: SelectTeamComponent },
          { name: 'businessSelect', component: SelectBusinessComponent },
          { name: 'industrySelect', component: SelectIndustryComponent },
          { name: 'productSelect', component: SelectProductServiceComponent },
          { name: 'accountSelect', component: SelectAccountComponent },
          { name: 'dealSelect', component: SelectDealComponent },
          { name: 'customAttributeValueSelect', component: SelectCustomAttributeValueComponent },
          { name: 'rule', component: RuleTypeComponent },
          { name: 'ruleset', component: RulesetTypeComponent },
          { name: 'ruleset-rules', component: RulesetRulesTypeComponent },
          { name: 'ng-select', component: SelectGenricFormlyComponent },
          { name: 'date-generic-select', component: GenericDateSetterComponent },
        ],
      }),
    ),

    // 4. Service Providers
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: CustomReuseStrategy, useExisting: RouteReuseStrategy },
    { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
    { provide: CDK_DRAG_CONFIG, useValue: DRAG_CONFIG },
    ProgressbarConfig,
    AudioRecordingService,

    // 5. Factory Providers
    {
      provide: APP_INITIALIZER,
      useFactory: AxiosConfigFactory,
      deps: [InterceptorService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppVersion,
      deps: [AppVersionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeClockSkew,
      deps: [ClockSkewService],
      multi: true,
    },

    // 6. Library Providers
    provideSweetAlert2(),
    provideEchartsCore({ echarts: () => import('echarts') }),
    provideNativeDateAdapter(),
    provideNgxMask(),
    provideNgxMatMomentDate(),
    provideSharedComponentTokens(),

  ],
};

